import React from 'react';
import {
  Row,
  Col,
  Card,
  Statistic,
  Space,
  Button,
  Tag,
  Empty,
  message,
  Grid,
  Divider,
  Select,
  Input,
  Popconfirm,
  Tabs,
  Segmented,
  Spin,
} from 'antd';
import {
  CheckCircleOutlined,
  DisconnectOutlined,
  ReloadOutlined,
  DatabaseOutlined,
  ApartmentOutlined,
  PlusOutlined,
  DeleteOutlined,
  UpOutlined,
  DownOutlined,
  WarningOutlined,
  RightOutlined,
} from '@ant-design/icons';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import axios from 'axios';
import { io } from 'socket.io-client';
import PlcCard from '../components/PlcCard';
import IndustrialMonitoringDashboard from '../components/IndustrialMonitoringDashboard';

const { useBreakpoint } = Grid;
const DEVICE_ONLINE_WINDOW_MS = 35000;

const CHART_RANGES = [
  { label: '1 min',  value: '1min',  limit: 60   },
  { label: '1 hora', value: '1hora', limit: 360  },
  { label: '1 dia',  value: '1dia',  limit: 1440 },
];

const DASHBOARD_MAIN_TAB_STORAGE_KEY = 'dashboard_active_main_tab';
const DEVICE_NAME_OVERRIDES_STORAGE_KEY = 'dashboard_device_name_overrides';

const CARD_THEMES = [
  { bg: '#fff7ed', border: '#fdba74', title: '#9a3412' },
  { bg: '#eff6ff', border: '#93c5fd', title: '#1d4ed8' },
  { bg: '#ecfeff', border: '#67e8f9', title: '#155e75' },
  { bg: '#f0fdf4', border: '#86efac', title: '#166534' },
  { bg: '#fefce8', border: '#fde047', title: '#854d0e' },
  { bg: '#fdf4ff', border: '#e879f9', title: '#86198f' },
  { bg: '#f8fafc', border: '#cbd5e1', title: '#334155' },
  { bg: '#fff1f2', border: '#fda4af', title: '#9f1239' },
];

const RECINTO_THEMES = [
  { bg: '#fffaf0', border: '#f59e0b', title: '#92400e' },
  { bg: '#f0f9ff', border: '#38bdf8', title: '#0c4a6e' },
  { bg: '#f0fdf4', border: '#4ade80', title: '#14532d' },
  { bg: '#fdf4ff', border: '#e879f9', title: '#701a75' },
  { bg: '#fff1f2', border: '#fb7185', title: '#881337' },
  { bg: '#f8fafc', border: '#94a3b8', title: '#334155' },
  { bg: '#ecfeff', border: '#22d3ee', title: '#155e75' },
  { bg: '#fefce8', border: '#facc15', title: '#854d0e' },
];

const PLANT_THEMES = [
  '#0ea5e9',
  '#16a34a',
  '#f59e0b',
  '#e11d48',
  '#7c3aed',
  '#0891b2',
  '#ca8a04',
  '#2563eb',
];

function MetricChip({ label, value, tone = 'neutral' }) {
  const tones = {
    neutral: { bg: '#eef2ff', border: '#c7d2fe', text: '#3730a3' },
    green:   { bg: '#ecfdf5', border: '#a7f3d0', text: '#065f46' },
    cyan:    { bg: '#ecfeff', border: '#a5f3fc', text: '#155e75' },
    amber:   { bg: '#fffbeb', border: '#fde68a', text: '#92400e' },
  };
  const style = tones[tone] || tones.neutral;
  return (
    <div style={{ borderRadius: 5, padding: '2px 3px', minHeight: 0, border: `1px solid ${style.border}`, background: style.bg, lineHeight: 1, textAlign: 'center' }}>
      <div style={{ fontSize: 8, color: '#475569', lineHeight: 1, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</div>
      <div style={{ fontSize: 11, fontWeight: 800, color: style.text, lineHeight: 1.1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{value}</div>
    </div>
  );
}

function authHeader() {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function getDeviceNameOverrides() {
  try {
    const raw = JSON.parse(localStorage.getItem(DEVICE_NAME_OVERRIDES_STORAGE_KEY) || '{}');
    return raw && typeof raw === 'object' ? raw : {};
  } catch {
    return {};
  }
}

function saveDeviceNameOverride(deviceId, name) {
  const clean = String(name || '').trim();
  const next = { ...getDeviceNameOverrides() };
  if (clean) next[deviceId] = clean;
  else delete next[deviceId];
  localStorage.setItem(DEVICE_NAME_OVERRIDES_STORAGE_KEY, JSON.stringify(next));
}

function parseDeviceTimestampMs(value) {
  if (!value) return NaN;
  const raw = String(value).trim();
  const normalized = raw.includes('T') ? raw : raw.replace(' ', 'T');
  const withZone = /Z$|[+-]\d{2}:?\d{2}$/.test(normalized) ? normalized : `${normalized}Z`;
  const ts = Date.parse(withZone);
  if (Number.isFinite(ts)) return ts;
  const fallback = Date.parse(raw);
  return Number.isFinite(fallback) ? fallback : NaN;
}

function isOnline(lastSeen, lastStatus, nowTs) {
  const now = nowTs !== undefined ? nowTs : Date.now();
  if (String(lastStatus || '').toLowerCase() === 'offline') return false;
  if (!lastSeen) return false;
  const ts = parseDeviceTimestampMs(lastSeen);
  if (!Number.isFinite(ts)) return false;
  return now - ts < DEVICE_ONLINE_WINDOW_MS;
}

function defaultHierarchy() {
  return { version: 1, plants: [], assignments: {} };
}

function normalizeAssignmentList(value) {
  const list = Array.isArray(value) ? value : value && typeof value === 'object' ? [value] : [];
  const out = [];
  const seen = new Set();
  list.forEach((item) => {
    const plantId   = String(item?.plantId   || '').trim();
    const recintoId = String(item?.recintoId || '').trim();
    if (!plantId || !recintoId) return;
    const key = `${plantId}::${recintoId}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push({ plantId, recintoId });
  });
  return out;
}

function sanitizeHierarchy(raw) {
  const src = raw && typeof raw === 'object' ? raw : {};
  const plants = Array.isArray(src.plants) ? src.plants : [];
  const cleanPlants = plants
    .map((p) => ({
      id:       String(p?.id   || '').trim(),
      name:     String(p?.name || '').trim(),
      color:    normalizeHexColor(p?.color),
      recintos: Array.isArray(p?.recintos)
        ? p.recintos
            .map((r) => ({ id: String(r?.id || '').trim(), name: String(r?.name || '').trim() }))
            .filter((r) => r.id && r.name)
        : [],
    }))
    .filter((p) => p.id && p.name);

  const validPairs = new Set();
  cleanPlants.forEach((p) => p.recintos.forEach((r) => validPairs.add(`${p.id}::${r.id}`)));

  const assignments = {};
  const srcAssignments = src.assignments && typeof src.assignments === 'object' ? src.assignments : {};
  Object.entries(srcAssignments).forEach(([deviceId, v]) => {
    const list = normalizeAssignmentList(v).filter((a) => validPairs.has(`${a.plantId}::${a.recintoId}`));
    if (list.length > 0) assignments[deviceId] = list;
  });

  return { version: 1, plants: cleanPlants, assignments };
}

function toNumber(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function formatUptime(seconds) {
  const n = Number(seconds);
  if (!Number.isFinite(n) || n < 0) return 'N/A';
  if (n < 60) return `${Math.floor(n)}s`;
  const d = Math.floor(n / 86400);
  const h = Math.floor((n % 86400) / 3600);
  const m = Math.floor((n % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function formatSystemUptime(seconds) {
  const total = Math.max(0, Math.floor(Number(seconds) || 0));
  const d = Math.floor(total / 86400);
  const h = Math.floor((total % 86400) / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const hh = String(h).padStart(2, '0');
  const mm = String(m).padStart(2, '0');
  const ss = String(s).padStart(2, '0');
  return d > 0 ? `${d}d ${hh}:${mm}:${ss}` : `${hh}:${mm}:${ss}`;
}

function makeId(prefix) {
  return `${prefix}-${Math.random().toString(36).slice(2, 9)}-${Date.now().toString(36)}`;
}

function normalizeHexColor(value, fallback = '#0ea5e9') {
  const v = String(value || '').trim();
  return /^#[0-9a-fA-F]{6}$/.test(v) ? v.toLowerCase() : fallback;
}

function pickTheme(deviceId) {
  const id = String(deviceId || '');
  let hash = 0;
  for (let i = 0; i < id.length; i += 1) hash = (hash * 31 + id.charCodeAt(i)) >>> 0;
  return CARD_THEMES[hash % CARD_THEMES.length];
}

function pickRecintoTheme(plantId, recintoId) {
  const id = `${String(plantId || '')}::${String(recintoId || '')}`;
  let hash = 0;
  for (let i = 0; i < id.length; i += 1) hash = (hash * 33 + id.charCodeAt(i)) >>> 0;
  return RECINTO_THEMES[hash % RECINTO_THEMES.length];
}

function mergeOrder(savedOrder, currentIds) {
  const incoming = Array.isArray(currentIds) ? currentIds : [];
  const validSet = new Set(incoming);
  const base = Array.isArray(savedOrder) ? savedOrder.filter((id) => validSet.has(id)) : [];
  incoming.forEach((id) => {
    if (!base.includes(id)) base.push(id);
  });
  return base;
}

function moveItem(list, fromId, toId) {
  const arr = Array.isArray(list) ? [...list] : [];
  const from = arr.indexOf(fromId);
  const to = arr.indexOf(toId);
  if (from < 0 || to < 0 || from === to) return arr;
  const [item] = arr.splice(from, 1);
  arr.splice(to, 0, item);
  return arr;
}

function getConnectivityTimes(device, nowTs) {
  const ts = parseDeviceTimestampMs(device?.last_seen);
  const online = isOnline(device?.last_seen, device?.last_status, nowTs);
  if (!Number.isFinite(ts)) return { connected: 'N/A', disconnected: 'N/A', online };
  const elapsedSec = Math.max(0, Math.floor((nowTs - ts) / 1000));
  return {
    connected:    online ? formatUptime(elapsedSec) : '0s',
    disconnected: online ? '0s' : formatUptime(elapsedSec),
    online,
  };
}

// ─── Componente reutilizable de tarjeta de dispositivo ────────────────────────
function DeviceCard({
  d, nowTs, isMobile,
  expanded, onToggle,
  range, onRangeChange,
  histData, loadingHist,
  nameDraft, onNameChange,
  savingName, onSaveName,
  theme,
}) {
  const conn       = getConnectivityTimes(d, nowTs);
  const online     = conn.online;
  const tempValue  = online && toNumber(d.latest_temp) != null ? `${toNumber(d.latest_temp).toFixed(1)} C`   : '--';
  const humValue   = online && toNumber(d.latest_hum)  != null ? `${toNumber(d.latest_hum).toFixed(0)} %`    : '--';
  const presValue  = online && toNumber(d.latest_pres) != null ? `${toNumber(d.latest_pres).toFixed(0)} hPa` : '--';
  const rssiValue  = online && toNumber(d.latest_rssi) != null ? `${toNumber(d.latest_rssi)} dBm`            : '--';

  return (
    <Card
      size='small'
      hoverable
      title={
        <div style={{ cursor: 'pointer', userSelect: 'none', color: theme?.title || '#111827' }} onClick={onToggle}>
          <Space>
            {d.name || d.device_id}
            {expanded
              ? <UpOutlined   style={{ fontSize: 11, color: '#94a3b8' }} />
              : <DownOutlined style={{ fontSize: 11, color: '#94a3b8' }} />}
          </Space>
        </div>
      }
      extra={<Space size={4}><Tag color={online ? 'green' : 'red'}>{online ? 'En linea' : 'Offline'}</Tag><Tag color='blue'>Arrastrar</Tag></Space>}
      style={{
        borderRadius: 9,
        boxShadow: '0 4px 10px rgba(15,23,42,0.06)',
        transition: 'all 0.2s',
        border: `1px solid ${theme?.border || '#d9e2ec'}`,
        background: theme?.bg || '#ffffff',
      }}
      bodyStyle={{ padding: 6 }}
    >
      {/* Vista colapsada: resumen compacto en una sola fila */}
      {!expanded && (
        <div style={{ cursor: 'pointer' }} onClick={onToggle}>
          <div style={{ fontSize: 9, color: '#94a3b8', marginBottom: 2 }}>ID: {d.device_id}</div>
          <Row gutter={[3, 3]}>
            <Col xs={6}><MetricChip tone='green'   label='Temp'  value={tempValue} /></Col>
            <Col xs={6}><MetricChip tone='cyan'    label='Hum'   value={humValue} /></Col>
            <Col xs={6}><MetricChip tone='amber'   label='Pres'  value={presValue} /></Col>
            <Col xs={6}><MetricChip tone='neutral' label='RSSI'  value={rssiValue} /></Col>
          </Row>
        </div>
      )}

      {/* Vista expandida: todos los tiles + gráficos */}
      {expanded && (
        <div>
          <div style={{ fontSize: 9, color: '#94a3b8', marginBottom: 2 }}>ID: {d.device_id}</div>
          <Row gutter={[3, 3]} style={{ marginBottom: 4 }}>
            <Col xs={6}><MetricChip tone='green'   label='Temp'       value={tempValue} /></Col>
            <Col xs={6}><MetricChip tone='cyan'    label='Hum'        value={humValue} /></Col>
            <Col xs={6}><MetricChip tone='amber'   label='Pres'       value={presValue} /></Col>
            <Col xs={6}><MetricChip tone='neutral' label='RSSI'       value={rssiValue} /></Col>
            <Col xs={8}><MetricChip tone='neutral' label='Encendido'  value={formatUptime(d.latest_uptime)} /></Col>
            <Col xs={8}><MetricChip tone='green'   label='Conect.'    value={conn.connected} /></Col>
            <Col xs={8}><MetricChip tone='amber'   label='Descon.'    value={conn.disconnected} /></Col>
          </Row>
          <Divider style={{ margin: '6px 0' }} />
          <Space direction={isMobile ? 'vertical' : 'horizontal'} style={{ width: '100%', marginBottom: 8 }}>
            <Input
              size='small'
              value={nameDraft ?? ''}
              onChange={(e) => onNameChange(e.target.value)}
              placeholder='Nombre del dispositivo'
            />
            <Button size='small' type='primary' onClick={onSaveName} loading={savingName}>Guardar</Button>
          </Space>
          <div style={{ marginBottom: 8 }}>
            <Segmented
              size='small'
              value={range}
              onChange={onRangeChange}
              options={CHART_RANGES.map((r) => ({ label: r.label, value: r.value }))}
            />
          </div>
          {loadingHist ? (
            <div style={{ textAlign: 'center', padding: 20 }}><Spin /></div>
          ) : histData.length === 0 ? (
            <Empty description='Sin datos historicos' image={Empty.PRESENTED_IMAGE_SIMPLE} />
          ) : (
            <>
              <div style={{ fontSize: 11, color: '#64748b', marginBottom: 4 }}>Temperatura / Humedad</div>
              <ResponsiveContainer width='100%' height={160}>
                <LineChart data={histData} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                  <XAxis dataKey='ts' tick={{ fontSize: 9 }} interval='preserveStartEnd' />
                  <YAxis tick={{ fontSize: 9 }} />
                  <Tooltip contentStyle={{ fontSize: 11 }} />
                  <Legend wrapperStyle={{ fontSize: 10 }} />
                  <Line type='monotone' dataKey='temp' name='Temp (C)'  stroke='#16a34a' dot={false} strokeWidth={1.5} connectNulls />
                  <Line type='monotone' dataKey='hum'  name='Hum (%)'   stroke='#0891b2' dot={false} strokeWidth={1.5} connectNulls />
                </LineChart>
              </ResponsiveContainer>
              <div style={{ fontSize: 11, color: '#64748b', marginTop: 8, marginBottom: 4 }}>Presion / RSSI</div>
              <ResponsiveContainer width='100%' height={140}>
                <LineChart data={histData} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                  <XAxis dataKey='ts' tick={{ fontSize: 9 }} interval='preserveStartEnd' />
                  <YAxis tick={{ fontSize: 9 }} />
                  <Tooltip contentStyle={{ fontSize: 11 }} />
                  <Legend wrapperStyle={{ fontSize: 10 }} />
                  <Line type='monotone' dataKey='pres' name='Pres (hPa)' stroke='#d97706' dot={false} strokeWidth={1.5} connectNulls />
                  <Line type='monotone' dataKey='rssi' name='RSSI (dBm)' stroke='#7c3aed' dot={false} strokeWidth={1.5} connectNulls />
                </LineChart>
              </ResponsiveContainer>
            </>
          )}
        </div>
      )}
    </Card>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────
export default function Dashboard() {
  const screens    = useBreakpoint();
  const isMobile   = !screens.md;

  const [devices,          setDevices]          = React.useState([]);
  const [loading,          setLoading]          = React.useState(false);
  const [nowTs,            setNowTs]            = React.useState(Date.now());
  const [plcs,             setPlcs]             = React.useState({ DELTA: { name: 'PLC Delta', status: 'offline' }, HAIWELL: { name: 'PLC Haiwell', status: 'offline' } });
  const [hierarchy,        setHierarchy]        = React.useState(defaultHierarchy());
  const [hierarchyLoading, setHierarchyLoading] = React.useState(false);
  const [savingHierarchy,  setSavingHierarchy]  = React.useState(false);
  const [newPlantName,     setNewPlantName]     = React.useState('');
  const [plantFilterQuery, setPlantFilterQuery] = React.useState('');
  const [newRecintoByPlant,setNewRecintoByPlant]= React.useState({});
  const [draggingPlantId,  setDraggingPlantId]  = React.useState('');
  const [uptimeBaseSec,    setUptimeBaseSec]    = React.useState(0);
  const [uptimeBaseTs,     setUptimeBaseTs]     = React.useState(Date.now());
  const [activeMainTab,    setActiveMainTab]    = React.useState(() => {
    const saved = localStorage.getItem(DASHBOARD_MAIN_TAB_STORAGE_KEY);
    const allowed = new Set(['plantas', 'panel', 'plcs', 'dispositivos', 'industrial']);
    return allowed.has(saved) ? saved : 'plantas';
  });
  const [activePlcTab,     setActivePlcTab]     = React.useState('DELTA');
  const [selectedPlantId,  setSelectedPlantId]  = React.useState('');
  const [nameDraftByDevice,setNameDraftByDevice]= React.useState({});
  const [savingNameByDevice,setSavingNameByDevice]= React.useState({});
  const [expandedDevices,  setExpandedDevices]  = React.useState({});
  const [deviceHistory,    setDeviceHistory]    = React.useState({});
  const [historyLoading,   setHistoryLoading]   = React.useState({});
  const [chartRange,       setChartRange]       = React.useState({});
  const [showGhosts,       setShowGhosts]       = React.useState(false);
  const [normalOrder,      setNormalOrder]      = React.useState([]);
  const [ghostOrder,       setGhostOrder]       = React.useState([]);
  const [roomOrderByKey,   setRoomOrderByKey]   = React.useState({});
  const [dragging,         setDragging]         = React.useState(null);
  const [pendingPlantColors, setPendingPlantColors] = React.useState({});

  React.useEffect(() => {
    localStorage.setItem(DASHBOARD_MAIN_TAB_STORAGE_KEY, activeMainTab);
  }, [activeMainTab]);

  // ── Cargar historial de un dispositivo ──────────────────────────────────────
  const fetchDeviceHistory = React.useCallback(async (deviceId, range) => {
    const r   = CHART_RANGES.find((x) => x.value === range) || CHART_RANGES[0];
    const key = `${deviceId}::${r.value}`;
    setHistoryLoading((prev) => ({ ...prev, [key]: true }));
    try {
      const res  = await axios.get(`/api/data/${deviceId}?limit=${r.limit * 6}`, { headers: authHeader() });
      const rows = res.data?.data || [];
      const sensorMap = {
        temp: 'temp', temperature: 'temp', in_temp_probe: 'temp',
        hum:  'hum',  humidity:    'hum',  in_humidity_probe: 'hum',
        pres: 'pres', pressure:    'pres', in_pressure_probe: 'pres',
        rssi: 'rssi',
      };
      const byNorm = {};
      rows.forEach((row) => {
        const norm = sensorMap[row.sensor_name] || row.sensor_name;
        if (!byNorm[norm]) byNorm[norm] = [];
        byNorm[norm].push(row);
      });
      Object.keys(byNorm).forEach((k) => { byNorm[k] = byNorm[k].slice(0, r.limit).reverse(); });
      const maxLen = Math.max(...Object.values(byNorm).map((a) => a.length), 0);
      const points = Array.from({ length: maxLen }, (_, i) => {
        const ref = (byNorm.temp || byNorm.hum || byNorm.pres || byNorm.rssi || [])[i];
        return {
          ts:   ref ? new Date(ref.timestamp).toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : String(i),
          temp: byNorm.temp?.[i] ? toNumber(byNorm.temp[i].value) : null,
          hum:  byNorm.hum?.[i]  ? toNumber(byNorm.hum[i].value)  : null,
          pres: byNorm.pres?.[i] ? toNumber(byNorm.pres[i].value) : null,
          rssi: byNorm.rssi?.[i] ? toNumber(byNorm.rssi[i].value) : null,
        };
      });
      setDeviceHistory((prev) => ({ ...prev, [key]: points }));
    } catch {
      message.error('No se pudo cargar historial');
    } finally {
      setHistoryLoading((prev) => ({ ...prev, [key]: false }));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleExpand = React.useCallback((deviceId) => {
    setExpandedDevices((prev) => {
      const next = { ...prev };
      if (next[deviceId]) {
        delete next[deviceId];
      } else {
        next[deviceId] = true;
        fetchDeviceHistory(deviceId, chartRange[deviceId] || '1min');
      }
      return next;
    });
  }, [chartRange, fetchDeviceHistory]);

  const changeChartRange = React.useCallback((deviceId, range) => {
    setChartRange((prev) => ({ ...prev, [deviceId]: range }));
    fetchDeviceHistory(deviceId, range);
  }, [fetchDeviceHistory]);

  // Helper que construye todas las props de DeviceCard para un dispositivo
  const buildCardProps = (d) => {
    const range   = chartRange[d.device_id] || '1min';
    const histKey = `${d.device_id}::${range}`;
    return {
      d, nowTs, isMobile,
      expanded:      !!expandedDevices[d.device_id],
      onToggle:      () => toggleExpand(d.device_id),
      range,
      onRangeChange: (v) => changeChartRange(d.device_id, v),
      histData:      deviceHistory[histKey] || [],
      loadingHist:   !!historyLoading[histKey],
      nameDraft:     nameDraftByDevice[d.device_id],
      onNameChange:  (val) => setNameDraftByDevice((prev) => ({ ...prev, [d.device_id]: val })),
      savingName:    !!savingNameByDevice[d.device_id],
      onSaveName:    () => saveDeviceName(d.device_id),
    };
  };

  // ── CRUD ────────────────────────────────────────────────────────────────────
  const loadDevices = React.useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get('/api/devices', { headers: authHeader() });
      const overrides = getDeviceNameOverrides();
      const merged = (res.data.devices || []).map((d) => ({
        ...d,
        name: overrides[d.device_id] || d.name,
      }));
      setDevices(merged);
    } catch {
      message.error('Error cargando dispositivos');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadHierarchy = React.useCallback(async () => {
    try {
      setHierarchyLoading(true);
      const res = await axios.get('/api/settings/plant-hierarchy', { headers: authHeader() });
      setHierarchy(sanitizeHierarchy(res.data?.hierarchy || defaultHierarchy()));
    } catch {
      setHierarchy(defaultHierarchy());
    } finally {
      setHierarchyLoading(false);
    }
  }, []);

  const saveHierarchy = React.useCallback(async (next, silent = false) => {
    const clean = sanitizeHierarchy(next);
    setHierarchy(clean);
    try {
      setSavingHierarchy(true);
      await axios.put('/api/settings/plant-hierarchy', { hierarchy: clean }, { headers: authHeader() });
      if (!silent) message.success('Jerarquia guardada');
    } catch {
      if (!silent) message.error('No se pudo guardar jerarquia');
    } finally {
      setSavingHierarchy(false);
    }
  }, []);

  const saveDeviceName = async (deviceId) => {
    const name = String(nameDraftByDevice[deviceId] || '').trim();
    if (!name) { message.error('El nombre no puede estar vacio'); return; }

    // Persist immediately in local storage to survive browser reloads.
    saveDeviceNameOverride(deviceId, name);
    setDevices((prev) => prev.map((d) => (d.device_id === deviceId ? { ...d, name } : d)));

    try {
      setSavingNameByDevice((prev) => ({ ...prev, [deviceId]: true }));
      await axios.put(`/api/devices/${deviceId}`, { name }, { headers: authHeader() });
      message.success('Nombre actualizado');
    } catch {
      message.warning('Nombre guardado localmente. No se pudo actualizar en servidor');
    } finally {
      setSavingNameByDevice((prev) => ({ ...prev, [deviceId]: false }));
    }
  };

  const loadSystemUptime = React.useCallback(async () => {
    try {
      const res = await axios.get('/api/ops/overview', { headers: authHeader() });
      const sec = Number(
        res.data?.host?.uptime_sec
        ?? res.data?.api?.uptime_sec
        ?? res.data?.api?.process_uptime_sec
        ?? 0
      );
      if (Number.isFinite(sec) && sec >= 0) {
        setUptimeBaseSec(Math.floor(sec));
        setUptimeBaseTs(Date.now());
      }
    } catch {
      // Silencioso para no ensuciar UX si este endpoint falla temporalmente.
    }
  }, []);

  // ── Effects ─────────────────────────────────────────────────────────────────
  React.useEffect(() => {
    const socket = io();
    socket.on('device:update', (update) => {
      if (String(update.deviceId || '').startsWith('PLC_')) {
        const plcKey = update.deviceId.replace('PLC_', '');
        setPlcs((prev) => ({ ...prev, [plcKey]: { ...prev[plcKey], status: update.data.status, data: update.data } }));
        return;
      }
      setNowTs(Date.now());
      loadDevices();
    });
    return () => socket.disconnect();
  }, [loadDevices]);

  React.useEffect(() => { loadDevices(); loadHierarchy(); }, [loadDevices, loadHierarchy]);
  React.useEffect(() => { const t = setInterval(() => setNowTs(Date.now()), 3000); return () => clearInterval(t); }, []);
  React.useEffect(() => { const p = setInterval(() => loadDevices(), 5000); return () => clearInterval(p); }, [loadDevices]);
  React.useEffect(() => {
    loadSystemUptime();
    const timer = setInterval(() => loadSystemUptime(), 15000);
    return () => clearInterval(timer);
  }, [loadSystemUptime]);

  React.useEffect(() => {
    const draft = {};
    devices.forEach((d) => { draft[d.device_id] = d.name || ''; });
    setNameDraftByDevice(draft);
  }, [devices]);

  // ── Derived state ────────────────────────────────────────────────────────────
  const onlineCount = devices.filter((d) => isOnline(d.last_seen, d.last_status, nowTs)).length
    + Object.values(plcs).filter((p) => p.status === 'online').length;
  const totalCount  = devices.length + Object.keys(plcs).length;
  const offlineCount = totalCount - onlineCount;

  // Separar dispositivos normales de fantasmas
  const { normalDevices, ghostDevices } = React.useMemo(() => {
    const normal = [];
    const ghost  = [];
    devices.forEach((d) => {
      const isGhost = d.type === 'autodetected'
        && !isOnline(d.last_seen, d.last_status, nowTs)
        && toNumber(d.latest_temp) == null
        && toNumber(d.latest_hum)  == null;
      if (isGhost) ghost.push(d);
      else         normal.push(d);
    });
    return { normalDevices: normal, ghostDevices: ghost };
  }, [devices, nowTs]);

  const orderedNormalIds = React.useMemo(
    () => mergeOrder(normalOrder, normalDevices.map((d) => d.device_id)),
    [normalDevices, normalOrder]
  );

  const orderedGhostIds = React.useMemo(
    () => mergeOrder(ghostOrder, ghostDevices.map((d) => d.device_id)),
    [ghostDevices, ghostOrder]
  );

  React.useEffect(() => {
    setNormalOrder((prev) => mergeOrder(prev, normalDevices.map((d) => d.device_id)));
  }, [normalDevices]);

  React.useEffect(() => {
    setGhostOrder((prev) => mergeOrder(prev, ghostDevices.map((d) => d.device_id)));
  }, [ghostDevices]);

  // ── Jerarquía ────────────────────────────────────────────────────────────────
  const addPlant = () => {
    const name = newPlantName.trim();
    if (!name) return;
    const next = JSON.parse(JSON.stringify(hierarchy));
    const newId = makeId('plant');
    const defaultColor = PLANT_THEMES[next.plants.length % PLANT_THEMES.length];
    next.plants.push({ id: newId, name, color: defaultColor, recintos: [] });
    setNewPlantName('');
    saveHierarchy(next);
  };

  const setPlantColor = (plantId, color) => {
    // Solo actualiza estado local; no persiste hasta que el usuario pulse Guardar
    setPendingPlantColors((prev) => ({ ...prev, [plantId]: color }));
  };

  const savePlantColor = (plantId) => {
    const color = pendingPlantColors[plantId];
    if (!color) return;
    const cleanColor = normalizeHexColor(color);
    const next = JSON.parse(JSON.stringify(hierarchy));
    const plant = next.plants.find((p) => p.id === plantId);
    if (!plant) return;
    plant.color = cleanColor;
    setPendingPlantColors((prev) => { const n = { ...prev }; delete n[plantId]; return n; });
    saveHierarchy(next, false);
  };

  const addRecinto = (plantId) => {
    const name = String(newRecintoByPlant[plantId] || '').trim();
    if (!name) return;
    const next  = JSON.parse(JSON.stringify(hierarchy));
    const plant = next.plants.find((p) => p.id === plantId);
    if (!plant) return;
    plant.recintos.push({ id: makeId('recinto'), name });
    setNewRecintoByPlant((prev) => ({ ...prev, [plantId]: '' }));
    saveHierarchy(next);
  };

  const deletePlant = (plantId) => {
    const next = JSON.parse(JSON.stringify(hierarchy));
    next.plants = next.plants.filter((p) => p.id !== plantId);
    Object.keys(next.assignments).forEach((k) => {
      const list = normalizeAssignmentList(next.assignments[k]).filter((a) => a.plantId !== plantId);
      if (list.length === 0) delete next.assignments[k];
      else next.assignments[k] = list;
    });
    saveHierarchy(next);
  };

  const reorderPlants = React.useCallback((fromPlantId, toPlantId) => {
    if (!fromPlantId || !toPlantId || fromPlantId === toPlantId) return;
    const next = JSON.parse(JSON.stringify(hierarchy));
    const fromIdx = next.plants.findIndex((p) => p.id === fromPlantId);
    const toIdx = next.plants.findIndex((p) => p.id === toPlantId);
    if (fromIdx < 0 || toIdx < 0 || fromIdx === toIdx) return;
    const [moved] = next.plants.splice(fromIdx, 1);
    next.plants.splice(toIdx, 0, moved);
    saveHierarchy(next, true);
  }, [hierarchy, saveHierarchy]);

  const setRecintoDevices = (plantId, recintoId, selectedDeviceIds) => {
    const next    = JSON.parse(JSON.stringify(hierarchy));
    const selected = new Set(selectedDeviceIds || []);
    const pairKey  = `${plantId}::${recintoId}`;
    devices.forEach((d) => {
      const current     = normalizeAssignmentList(next.assignments[d.device_id]);
      const withoutPair = current.filter((a) => `${a.plantId}::${a.recintoId}` !== pairKey);
      if (selected.has(d.device_id)) {
        next.assignments[d.device_id] = [...withoutPair, { plantId, recintoId }];
      } else if (withoutPair.length > 0) {
        next.assignments[d.device_id] = withoutPair;
      } else {
        delete next.assignments[d.device_id];
      }
    });
    saveHierarchy(next, true);
  };

  const resolvedAssignments = React.useMemo(() => {
    const out = {};
    Object.entries(hierarchy.assignments || {}).forEach(([deviceId, value]) => {
      const list = normalizeAssignmentList(value);
      if (list.length > 0) out[deviceId] = list;
    });
    return out;
  }, [hierarchy]);

  const devicesByRecinto = React.useMemo(() => {
    const map = {};
    hierarchy.plants.forEach((plant) => {
      plant.recintos.forEach((recinto) => {
        const key = `${plant.id}::${recinto.id}`;
        map[key] = devices
          .filter((d) => {
            const list = resolvedAssignments[d.device_id] || [];
            return list.some((a) => a.plantId === plant.id && a.recintoId === recinto.id);
          })
          .map((d) => d.device_id);
      });
    });
    return map;
  }, [devices, hierarchy, resolvedAssignments]);

  const deviceOptions = React.useMemo(
    () => devices.map((d) => ({ value: d.device_id, label: `${d.name || d.device_id} (${d.device_id})` })),
    [devices]
  );

  const deviceById = React.useMemo(() => {
    const map = {};
    devices.forEach((d) => { map[d.device_id] = d; });
    return map;
  }, [devices]);

  React.useEffect(() => {
    if (hierarchy.plants.length === 0) {
      if (selectedPlantId !== '') setSelectedPlantId('');
      return;
    }
    // Permite colapsar manualmente (selectedPlantId === '').
    // Solo reasigna si la planta seleccionada ya no existe.
    if (selectedPlantId && !hierarchy.plants.some((p) => p.id === selectedPlantId)) {
      setSelectedPlantId(hierarchy.plants[0].id);
    }
  }, [hierarchy, selectedPlantId]);

  const selectedPlant = React.useMemo(
    () => hierarchy.plants.find((p) => p.id === selectedPlantId) || null,
    [hierarchy, selectedPlantId]
  );

  const plantCards = React.useMemo(() => {
    return hierarchy.plants.map((plant) => {
      const ids = new Set();
      plant.recintos.forEach((r) => {
        const key = `${plant.id}::${r.id}`;
        (devicesByRecinto[key] || []).forEach((id) => ids.add(id));
      });
      const deviceIds = Array.from(ids);
      const onlineDevices = deviceIds
        .map((id) => deviceById[id])
        .filter((d) => d && isOnline(d.last_seen, d.last_status, nowTs));
      const online = onlineDevices.length;

      const temps = onlineDevices.map((d) => toNumber(d.latest_temp)).filter((v) => v != null);
      const hums  = onlineDevices.map((d) => toNumber(d.latest_hum)).filter((v) => v != null);
      const avgTemp = temps.length ? temps.reduce((a, b) => a + b, 0) / temps.length : null;
      const avgHum  = hums.length  ? hums.reduce((a, b) => a + b, 0)  / hums.length  : null;

      const lastSeenMs = deviceIds
        .map((id) => { const d = deviceById[id]; return d ? parseDeviceTimestampMs(d.last_seen) : NaN; })
        .filter(Number.isFinite)
        .reduce((max, v) => (v > max ? v : max), 0);

      return {
        id: plant.id,
        name: plant.name,
        color: normalizeHexColor(plant.color, PLANT_THEMES[0]),
        recintoNames: plant.recintos.map((r) => r.name),
        recintos: plant.recintos.length,
        devices: deviceIds.length,
        online,
        avgTemp,
        avgHum,
        lastSeenMs: lastSeenMs || null,
      };
    });
  }, [hierarchy, devicesByRecinto, deviceById, nowTs]);

  const filteredPlantCards = React.useMemo(() => {
    const q = plantFilterQuery.trim().toLowerCase();
    if (!q) return plantCards;
    return plantCards.filter((p) => p.name.toLowerCase().includes(q));
  }, [plantCards, plantFilterQuery]);

  const plcTabItems = React.useMemo(
    () => [
      { key: 'DELTA',   label: `PLC Delta (${plcs.DELTA?.status || 'offline'})`,     children: <PlcCard plc={plcs.DELTA} /> },
      { key: 'HAIWELL', label: `PLC Haiwell (${plcs.HAIWELL?.status || 'offline'})`, children: <PlcCard plc={plcs.HAIWELL} /> },
      {
        key: 'ALL', label: 'Vista general PLCs',
        children: (
          <Row gutter={[16, 16]}>
            <Col xs={24} lg={12}><PlcCard plc={plcs.DELTA} /></Col>
            <Col xs={24} lg={12}><PlcCard plc={plcs.HAIWELL} /></Col>
          </Row>
        ),
      },
    ],
    [plcs]
  );

  const calcRecintoMetrics = (roomDevices) => {
    const fresh = roomDevices.filter((d) => isOnline(d.last_seen, d.last_status, nowTs));
    return {
      count:  roomDevices.length,
      online: fresh.length,
    };
  };

  const liveSystemUptimeSec = React.useMemo(
    () => uptimeBaseSec + Math.max(0, Math.floor((Date.now() - uptimeBaseTs) / 1000)),
    [uptimeBaseSec, uptimeBaseTs]
  );

  const reorderScope = React.useCallback((scope, fromId, toId) => {
    if (!scope || !fromId || !toId || fromId === toId) return;
    if (scope === 'normal') {
      setNormalOrder((prev) => moveItem(mergeOrder(prev, orderedNormalIds), fromId, toId));
      return;
    }
    if (scope === 'ghost') {
      setGhostOrder((prev) => moveItem(mergeOrder(prev, orderedGhostIds), fromId, toId));
      return;
    }
    if (scope.startsWith('room::')) {
      const roomKey = scope.replace('room::', '');
      const current = devicesByRecinto[roomKey] || [];
      setRoomOrderByKey((prev) => {
        const nextRoomOrder = mergeOrder(prev[roomKey], current);
        return { ...prev, [roomKey]: moveItem(nextRoomOrder, fromId, toId) };
      });
    }
  }, [devicesByRecinto, orderedGhostIds, orderedNormalIds]);

  const wireDragProps = React.useCallback((scope, deviceId) => ({
    draggable: true,
    onDragStart: () => setDragging({ scope, deviceId }),
    onDragOver: (e) => e.preventDefault(),
    onDrop: (e) => {
      e.preventDefault();
      if (dragging?.scope === scope) reorderScope(scope, dragging?.deviceId, deviceId);
      setDragging(null);
    },
    onDragEnd: () => setDragging(null),
  }), [dragging, reorderScope]);

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div>
      <Tabs
        activeKey={activeMainTab}
        onChange={setActiveMainTab}
        items={[

          // ──────────────── TAB 1: DASHBOARD PLANTAS ────────────────
          {
            key: 'plantas',
            label: 'Dashboard Plantas',
            children: (
              <>
                <h2 style={{ marginBottom: 12 }}><ApartmentOutlined /> Plantas</h2>

                <Card
                  size='small'
                  style={{
                    marginBottom: 12,
                    borderRadius: 12,
                    border: '1px solid #0ea5e9',
                    background: 'linear-gradient(120deg, #f0f9ff, #ecfeff)',
                  }}
                >
                  <Space direction='vertical' size={2}>
                    <div style={{ fontSize: 13, color: '#0c4a6e', fontWeight: 700 }}>DISPONIBILIDAD OPERATIVA DEL HOST</div>
                    <div style={{ fontSize: isMobile ? 30 : 44, lineHeight: 1, fontWeight: 800, color: '#0f172a', letterSpacing: 1 }}>
                      {formatSystemUptime(liveSystemUptimeSec)}
                    </div>
                  </Space>
                </Card>

                <Card size='small' style={{ marginBottom: 12 }}>
                  <Space direction={isMobile ? 'vertical' : 'horizontal'} style={{ width: '100%' }}>
                    <Input
                      placeholder='Nombre de nueva planta'
                      value={newPlantName}
                      onChange={(e) => setNewPlantName(e.target.value)}
                      style={{ minWidth: isMobile ? '100%' : 280 }}
                    />
                    <Button type='primary' icon={<PlusOutlined />} onClick={addPlant} loading={savingHierarchy} style={{ minHeight: 44, minWidth: isMobile ? '100%' : 'auto' }}>
                      Crear planta
                    </Button>
                    <Input
                      placeholder='Buscar planta...'
                      value={plantFilterQuery}
                      onChange={(e) => setPlantFilterQuery(e.target.value)}
                      allowClear
                      style={{ minWidth: isMobile ? '100%' : 240 }}
                    />
                  </Space>
                </Card>

                {plantCards.length === 0 ? (
                  <Card size='small'><Empty description='No hay plantas creadas' /></Card>
                ) : filteredPlantCards.length === 0 ? (
                  <Card size='small'><Empty description='No hay plantas que coincidan con el filtro' /></Card>
                ) : (
                  <Row gutter={[isMobile ? 8 : 12, isMobile ? 8 : 12]} style={{ marginBottom: isMobile ? 8 : 12 }}>
                    {filteredPlantCards.map((plant) => (
                      <React.Fragment key={plant.id}>
                        <Col xs={24} sm={12} md={8} lg={8} xl={6}>
                          <Card
                            size='small'
                            hoverable
                            draggable
                            onDragStart={() => setDraggingPlantId(plant.id)}
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={(e) => {
                              e.preventDefault();
                              reorderPlants(draggingPlantId, plant.id);
                              setDraggingPlantId('');
                            }}
                            onDragEnd={() => setDraggingPlantId('')}
                            onClick={() => setSelectedPlantId((prev) => (prev === plant.id ? '' : plant.id))}
                            style={{
                              border: selectedPlantId === plant.id ? `2px solid ${plant.color}` : `1px solid ${plant.color}`,
                              borderTop: `5px solid ${plant.color}`,
                              background: 'linear-gradient(180deg, #ffffff, #f8fafc)',
                              borderRadius: 10,
                              minHeight: 92,
                              cursor: 'grab',
                              boxShadow: selectedPlantId === plant.id
                                ? '0 6px 14px rgba(15,23,42,0.14)'
                                : '0 4px 10px rgba(15,23,42,0.06)',
                            }}
                            bodyStyle={{ padding: isMobile ? 8 : 10 }}
                            headStyle={{ padding: isMobile ? '6px 10px' : '8px 10px', minHeight: 'auto' }}
                          >
                            <Space direction='vertical' size={5} style={{ width: '100%' }}>
                              {/* Nombre + flecha */}
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <strong style={{ fontSize: 14, color: '#0f172a', lineHeight: 1.2 }}>{plant.name}</strong>
                                {selectedPlantId === plant.id
                                  ? <DownOutlined  style={{ color: plant.color, fontSize: 13 }} />
                                  : <RightOutlined style={{ color: '#94a3b8', fontSize: 13 }} />}
                              </div>

                              {/* Contadores */}
                              <Space wrap size={[4, 4]}>
                                <Tag color='blue'     style={{ fontSize: 10, margin: 0, lineHeight: 1.5 }}>{plant.recintos} recinto{plant.recintos !== 1 ? 's' : ''}</Tag>
                                <Tag color='geekblue' style={{ fontSize: 10, margin: 0, lineHeight: 1.5 }}>{plant.devices} disp.</Tag>
                                <Tag color='green'    style={{ fontSize: 10, margin: 0, lineHeight: 1.5 }}>{plant.online} online</Tag>
                                {(plant.devices - plant.online) > 0 && (
                                  <Tag color='red' style={{ fontSize: 10, margin: 0, lineHeight: 1.5 }}>{plant.devices - plant.online} offline</Tag>
                                )}
                              </Space>

                              {/* Barra de disponibilidad */}
                              {plant.devices > 0 && (
                                <div style={{ width: '100%' }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9, color: '#64748b', marginBottom: 2 }}>
                                    <span>Disponibilidad</span>
                                    <span>{Math.round((plant.online / plant.devices) * 100)}%</span>
                                  </div>
                                  <div style={{ background: '#e2e8f0', borderRadius: 4, height: 5, overflow: 'hidden' }}>
                                    <div style={{ width: `${(plant.online / plant.devices) * 100}%`, height: '100%', background: plant.online === plant.devices ? '#16a34a' : plant.online === 0 ? '#ef4444' : '#f59e0b', borderRadius: 4, transition: 'width 0.4s' }} />
                                  </div>
                                </div>
                              )}

                              {/* Métricas promedio */}
                              {(plant.avgTemp != null || plant.avgHum != null) && (
                                <div style={{ display: 'flex', gap: 6 }}>
                                  {plant.avgTemp != null && (
                                    <div style={{ flex: 1, background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 5, padding: '2px 5px', textAlign: 'center' }}>
                                      <div style={{ fontSize: 8, color: '#475569', textTransform: 'uppercase', fontWeight: 600 }}>Temp prom.</div>
                                      <div style={{ fontSize: 12, fontWeight: 800, color: '#065f46' }}>{plant.avgTemp.toFixed(1)}°C</div>
                                    </div>
                                  )}
                                  {plant.avgHum != null && (
                                    <div style={{ flex: 1, background: '#ecfeff', border: '1px solid #a5f3fc', borderRadius: 5, padding: '2px 5px', textAlign: 'center' }}>
                                      <div style={{ fontSize: 8, color: '#475569', textTransform: 'uppercase', fontWeight: 600 }}>Hum prom.</div>
                                      <div style={{ fontSize: 12, fontWeight: 800, color: '#155e75' }}>{plant.avgHum.toFixed(0)}%</div>
                                    </div>
                                  )}
                                </div>
                              )}

                              {/* Recintos */}
                              {plant.recintoNames.length > 0 && (
                                <div style={{ fontSize: 9, color: '#64748b', lineHeight: 1.4 }}>
                                  <span style={{ fontWeight: 600, color: '#475569' }}>Recintos: </span>
                                  {plant.recintoNames.slice(0, 4).join(', ')}{plant.recintoNames.length > 4 ? ` +${plant.recintoNames.length - 4} más` : ''}
                                </div>
                              )}

                              {/* Última actualización */}
                              {plant.lastSeenMs && (
                                <div style={{ fontSize: 9, color: '#94a3b8' }}>
                                  Actualizado: {new Date(plant.lastSeenMs).toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' })}
                                </div>
                              )}

                              {/* Color picker */}
                              <Space size={4} onClick={(e) => e.stopPropagation()}>
                                <span style={{ fontSize: 9, color: '#94a3b8' }}>Color:</span>
                                <Input
                                  type='color'
                                  size='small'
                                  value={pendingPlantColors[plant.id] ?? plant.color}
                                  onChange={(e) => setPlantColor(plant.id, e.target.value)}
                                  style={{ width: 26, height: 20, padding: 1, cursor: 'pointer' }}
                                />
                                {pendingPlantColors[plant.id] && (
                                  <Button
                                    size='small'
                                    type='primary'
                                    loading={savingHierarchy}
                                    onClick={() => savePlantColor(plant.id)}
                                    style={{ fontSize: 10, padding: '0 6px', height: 20, lineHeight: 1 }}
                                  >
                                    Guardar
                                  </Button>
                                )}
                              </Space>
                            </Space>
                          </Card>
                        </Col>

                        {selectedPlant && selectedPlant.id === plant.id && (
                          <Col xs={24}>
                            <Card
                              size='small'
                              title={<Space><strong>{selectedPlant.name}</strong><Tag color='purple'>Detalle por recintos</Tag></Space>}
                              extra={
                                <Space>
                                  <Button size='small' onClick={() => setSelectedPlantId('')}>Contraer</Button>
                                  <Popconfirm title='Eliminar planta' onConfirm={() => deletePlant(selectedPlant.id)}>
                                    <Button size='small' danger icon={<DeleteOutlined />} />
                                  </Popconfirm>
                                </Space>
                              }
                            >
                              <Space direction={isMobile ? 'vertical' : 'horizontal'} style={{ width: '100%' }}>
                                <Input
                                  placeholder={`Nuevo recinto para ${selectedPlant.name}`}
                                  value={newRecintoByPlant[selectedPlant.id] || ''}
                                  onChange={(e) => setNewRecintoByPlant((prev) => ({ ...prev, [selectedPlant.id]: e.target.value }))}
                                  style={{ minWidth: isMobile ? '100%' : 280 }}
                                />
                                <Button icon={<PlusOutlined />} onClick={() => addRecinto(selectedPlant.id)} loading={savingHierarchy} style={{ minHeight: 44, minWidth: isMobile ? '100%' : 'auto' }}>
                                  Agregar recinto
                                </Button>
                              </Space>

                              <Divider />

                              {selectedPlant.recintos.length === 0 ? (
                                <Empty description='Esta planta no tiene recintos' />
                              ) : (
                                <Row gutter={[12, 12]}>
                                  {selectedPlant.recintos.map((r) => {
                                    const recintoKey        = `${selectedPlant.id}::${r.id}`;
                                    const recintoTheme      = pickRecintoTheme(selectedPlant.id, r.id);
                                    const selectedInRecinto = devicesByRecinto[recintoKey] || [];
                                    const orderedRoomIds    = mergeOrder(roomOrderByKey[recintoKey], selectedInRecinto);
                                    const roomDevices       = orderedRoomIds.map((id) => deviceById[id]).filter(Boolean);
                                    const metrics           = calcRecintoMetrics(roomDevices);
                                    return (
                                      <Col key={r.id} xs={24} lg={12}>
                                        <Card
                                          size='small'
                                          title={
                                            <Space>
                                              <strong style={{ color: recintoTheme.title }}>{r.name}</strong>
                                              <Tag color='blue'>{metrics.count} dispositivos</Tag>
                                              <Tag color='green'>{metrics.online} en linea</Tag>
                                            </Space>
                                          }
                                          style={{
                                            borderRadius: 10,
                                            boxShadow: '0 4px 12px rgba(15,23,42,0.07)',
                                            border: `1px solid ${recintoTheme.border}`,
                                            background: recintoTheme.bg,
                                          }}
                                        >
                                          <Select
                                            mode='multiple'
                                            allowClear
                                            showSearch
                                            optionFilterProp='label'
                                            placeholder='Agregar o quitar dispositivos de este recinto'
                                            options={deviceOptions}
                                            value={selectedInRecinto}
                                            onChange={(vals) => setRecintoDevices(selectedPlant.id, r.id, vals)}
                                            loading={savingHierarchy || hierarchyLoading}
                                            style={{ width: '100%', marginBottom: roomDevices.length > 0 ? 10 : 0 }}
                                          />

                                          {/* Tarjetas individuales expandibles de dispositivos del recinto */}
                                          {roomDevices.length > 0 && (
                                            <Row gutter={[10, 10]}>
                                              {roomDevices.map((d) => (
                                                <Col key={d.device_id} xs={24} sm={12} md={6} lg={12} xl={8}>
                                                  <div {...wireDragProps(`room::${recintoKey}`, d.device_id)} style={{ cursor: 'grab' }}>
                                                    <DeviceCard {...buildCardProps(d)} theme={pickTheme(d.device_id)} />
                                                  </div>
                                                </Col>
                                              ))}
                                            </Row>
                                          )}
                                        </Card>
                                      </Col>
                                    );
                                  })}
                                </Row>
                              )}
                            </Card>
                          </Col>
                        )}
                      </React.Fragment>
                    ))}
                  </Row>
                )}
              </>
            ),
          },

          // ──────────────── TAB 2: PANEL ────────────────
          {
            key: 'panel',
            label: 'Panel de Dispositivos',
            children: (
              <>
                <h1 style={{ marginBottom: 16 }}>Panel de Dispositivos</h1>
                <Space style={{ marginBottom: 12 }}>
                  <Button size='small' icon={<ReloadOutlined />} onClick={loadDevices} loading={loading}>Actualizar</Button>
                </Space>
                <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
                  <Col xs={24} sm={8}><Card size='small'><Statistic title='Total dispositivos' value={totalCount} /></Card></Col>
                  <Col xs={24} sm={8}><Card size='small'><Statistic title='En linea' value={onlineCount} prefix={<CheckCircleOutlined />} valueStyle={{ color: '#16a34a' }} /></Card></Col>
                  <Col xs={24} sm={8}><Card size='small'><Statistic title='Fuera de linea' value={offlineCount} prefix={<DisconnectOutlined />} valueStyle={{ color: '#dc2626' }} /></Card></Col>
                </Row>
              </>
            ),
          },

          // ──────────────── TAB 3: PLCs ────────────────
          {
            key: 'plcs',
            label: 'Dashboard PLC',
            children: (
              <>
                <h2 style={{ marginBottom: 12 }}><DatabaseOutlined /> Control Industrial (PLCs)</h2>
                <Card size='small' style={{ marginBottom: 12 }}>
                  <Tabs activeKey={activePlcTab} onChange={setActivePlcTab} items={plcTabItems} />
                </Card>
              </>
            ),
          },

          // ──────────────── TAB 4: DISPOSITIVOS ────────────────
          {
            key: 'dispositivos',
            label: 'Dashboard Dispositivos',
            children: (
              <>
                <Card
                  size='small'
                  style={{
                    marginBottom: 12,
                    borderRadius: 10,
                    border: '1px solid #334155',
                    background: 'linear-gradient(120deg, #0f172a, #1e293b)',
                  }}
                >
                  <div style={{ color: '#f8fafc', fontWeight: 800, letterSpacing: 0.6 }}>
                    BANCO DE CODIGOS Y ARQUITECTURAS DE CONEXION
                  </div>
                </Card>
                <h2 style={{ marginBottom: 12 }}>Lista general de dispositivos</h2>
                {devices.length === 0 ? (
                  <Card size='small'><Empty description='No hay dispositivos registrados' /></Card>
                ) : (
                  <>
                    {/* Dispositivos configurados */}
                    {normalDevices.length === 0 ? (
                      <Card size='small' style={{ marginBottom: 12 }}>
                        <Empty description='No hay dispositivos configurados' />
                      </Card>
                    ) : (
                      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
                        {orderedNormalIds.map((deviceId) => {
                          const d = deviceById[deviceId];
                          if (!d) return null;
                          return (
                          <Col key={d.device_id} xs={24} sm={12} md={6} lg={6} xl={4}>
                            <div {...wireDragProps('normal', d.device_id)} style={{ cursor: 'grab' }}>
                              <DeviceCard {...buildCardProps(d)} theme={pickTheme(d.device_id)} />
                            </div>
                          </Col>
                          );
                        })}
                      </Row>
                    )}

                    {/* Dispositivos fantasma: autodetected + offline + sin telemetría */}
                    {ghostDevices.length > 0 && (
                      <Card
                        size='small'
                        style={{ borderRadius: 8, border: '1px dashed #fbbf24', background: '#fffbeb', marginBottom: 12 }}
                        title={
                          <Space>
                            <WarningOutlined style={{ color: '#d97706' }} />
                            <span style={{ color: '#92400e', fontWeight: 600 }}>
                              Nuevos dispositivos detectados ({ghostDevices.length}) — sin configurar
                            </span>
                            <Button size='small' onClick={() => setShowGhosts((v) => !v)}>
                              {showGhosts ? 'Ocultar' : 'Mostrar'}
                            </Button>
                          </Space>
                        }
                      >
                        {showGhosts ? (
                          <Row gutter={[12, 12]}>
                            {orderedGhostIds.map((deviceId) => {
                              const d = deviceById[deviceId];
                              if (!d) return null;
                              return (
                              <Col key={d.device_id} xs={24} sm={12} md={6} lg={6} xl={4}>
                                <div {...wireDragProps('ghost', d.device_id)} style={{ cursor: 'grab' }}>
                                  <DeviceCard {...buildCardProps(d)} theme={pickTheme(d.device_id)} />
                                </div>
                              </Col>
                              );
                            })}
                          </Row>
                        ) : (
                          <div style={{ color: '#92400e', fontSize: 12 }}>
                            Dispositivos detectados por MQTT sin telemetria aun.
                            Haz clic en "Mostrar" para verlos y asignarles un nombre.
                          </div>
                        )}
                      </Card>
                    )}
                  </>
                )}
              </>
            ),
          },

          // ──────────────── TAB 5: CENTRO MONITOREO INDUSTRIAL ────────────────
          {
            key: 'industrial',
            label: 'Centro de Monitoreo Industrial',
            children: <IndustrialMonitoringDashboard />,
          },

        ]}
      />
    </div>
  );
}
